import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useFormFields } from 'helpers';
import {
  Alerts,
  ButtonAction,
  Select,
  Modal,
  WideContainer,
  PageContent,
  Pagination,
  Table,
  Spinner,
  FiltersControls,
  FiltersCustom,
} from 'components';
import {
  loadMaPeopleUsersCreator,
  loadGlobalMaRolesCreator,
  setMaPeopleUsersSortCreator,
  changeMaPeopleUsersRoleCreator,
  deleteMaPeopleUserCreator,
  setMaPeopleUsersPageCreator,
  setMaPeopleUsersPerPageCreator,
  downloadMaPeopleReportCreator,
  setMaPeopleUsersFiltersCreator,
} from 'store/actionsCreators';
import { ReactSVG } from 'react-svg';
import { iconList } from 'assets';
import { Margin } from 'styled-components-spacing';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Content, Wrapper, Icon, RowStyled } from '../List/List.styled';
import { formatDate } from 'helpers/date';

function Users({
  items,
  page,
  pages,
  perPage,
  sort,
  loadUsers,
  roles,
  loadGlobalMaRoles,
  setSort,
  loading,
  changeRole,
  deleteUser,
  setPage,
  setPerPage,
  downloadReport,
  filters,
  setFilters,
}) {
  const { t } = useTranslation();
  const [removeConfirmModal, setRemoveConfirmModal] = useState(null);
  const [filterFields, handleFilterFieldChange] = useFormFields({
    firstName: '',
    lastName: '',
    email: '',
    maFrom: null,
    role: null,
  });

  const filtersProps = {
    firstName: {
      type: 'input',
      name: 'first-name',
      id: 'f-name',
      label: t('page.ma.peopleAdd.firstName'),
      placeholder: t('forms.firstNamePlaceholder'),
    },
    lastName: {
      type: 'input',
      name: 'last-name',
      id: 'f-lastName',
      label: t('page.ma.peopleAdd.lastName'),
      placeholder: t('forms.lastNamePlaceholder'),
    },
    email: {
      type: 'input',
      name: 'email',
      id: 'f-email',
      label: t('page.ma.peopleAdd.email'),
      placeholder: t('forms.lastNamePlaceholder'),
    },
    maFrom: {
      type: 'datepicker',
      name: 'maFrom',
      id: 'f-maFrom',
      label: t('page.ma.peopleUsers.maFrom'),
      placeholder: t('forms.createdFromPlaceholder'),
    },
    role: {
      type: 'select',
      name: 'role',
      id: 'f-role',
      label: t('page.ma.peopleUsers.role'),
      options: roles,
    },
  };

  useEffect(() => {
    setPage(1);
  }, [perPage, filters]);

  useEffect(() => {
    loadUsers();
  }, [page, perPage, sort, filters]);

  useEffect(() => {
    if (!roles.length) {
      loadGlobalMaRoles();
    }
  }, []);

  const handleSortBy = (name) => () => {
    const [currentSortName, currentSortStatus] = sort || [];
    const sortByNewColumn = currentSortName !== name;

    if (sortByNewColumn) {
      setSort([name, 'desc']);
      return;
    }

    const newSortStatus = currentSortStatus === 'desc' ? 'asc' : 'desc';
    setSort([name, newSortStatus]);
  };

  const handleChangeRole = (userId, oldRoleId) => ({ value }) => {
    changeRole({ user: userId, oldRole: oldRoleId, role: value });
  };

  const handleDeleteUser = (id) => () => {
    deleteUser(id);
    return true;
  };

  const handleChangePerPage = ({ value }) => {
    setPerPage(value);
  };

  const loadRoles = (_roles, rolesArr, currentId) => {
    const filterRoles = rolesArr.filter(({ role }) => role.id !== currentId);
    const list = _roles.filter(({ value }) => {
      const check = filterRoles.filter(({ role }) => role.id === value);
      if (check.length) {
        return false;
      }
      return true;
    });
    if (list.length && rolesArr.length >= 2) {
      list.unshift({
        label: 'None',
        value: 0,
      });
    }
    return list;
  };

  const downloadButton = () => (
    <Wrapper onClick={() => downloadReport()}>
      <Icon>
        <ReactSVG src={iconList.download} />
      </Icon>
      <Content>
        <p className="word-break">{t('page.ma.peopleUsers.reportTitle')}</p>
        <Margin top="4">
          <div className="link uppercase">{t('page.ma.peopleList.downloadReport')}</div>
        </Margin>
      </Content>
    </Wrapper>
  );

  return (
    <>
      <WideContainer>
        <PageContent>
          <Alerts type="ma" />
          <Margin top={{ xs: 16 }}>
            <FiltersCustom
              title={t('page.ma.peopleAdd.filterTitle')}
              filters={filtersProps}
              filterFields={filterFields}
              handleFilterFieldChange={handleFilterFieldChange}
              onFiltersApply={setFilters}
            />
          </Margin>
          <Margin top={{ xs: 16 }}>
            <RowStyled>
              {downloadButton()}
              <FiltersControls perPageOnChange={handleChangePerPage} perPageSelected={perPage} />
            </RowStyled>
          </Margin>
          <Table tablebreakpoint="xl">
            <Table.Head>
              <Table.Row>
                <Table.Heading sort={handleSortBy('firstLastName')}>
                  {t('page.ma.peopleUsers.firstAndLastName')}
                </Table.Heading>
                <Table.Heading sort={handleSortBy('email')}>
                  {t('page.ma.peopleUsers.email')}
                </Table.Heading>
                <Table.Heading sort={handleSortBy('role')}>
                  {t('page.ma.peopleUsers.role')}
                </Table.Heading>
                <Table.Heading />
                <Table.Heading />
                <Table.Heading sort={handleSortBy('date')}>
                  {t('page.ma.peopleUsers.maFrom')}
                </Table.Heading>
                <Table.Heading />
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {loading ? (
                <Table.Row>
                  <Table.Cell colSpan={6}>
                    <Spinner />
                  </Table.Cell>
                </Table.Row>
              ) : null}
              {!loading && !items.length ? (
                <Table.Row>
                  <Table.Cell colSpan={6}>{t('global.noResultsFound')}</Table.Cell>
                </Table.Row>
              ) : null}
              {!loading && items.length
                ? items.map((user) => (
                    <Table.Row key={user.id}>
                      <Table.Cell>
                        <Table.Label>{t('page.ma.peopleUsers.firstAndLastName')}:</Table.Label>
                        <Link to={`/user-profile/${user.uniqueUrl}`}>
                          {`${user.firstName} ${user.lastName}`}
                        </Link>
                      </Table.Cell>
                      <Table.Cell>
                        <Table.Label>{t('page.ma.peopleUsers.email')}:</Table.Label>
                        {user.email}
                      </Table.Cell>
                      <Table.Cell>
                        <Table.Label>{t('page.ma.peopleUsers.role')}:</Table.Label>
                        <Select
                          small
                          value={
                            user.memberAssociationUserRoles[0]
                              ? user.memberAssociationUserRoles[0].role
                              : null
                          }
                          onChange={handleChangeRole(
                            user.id,
                            user.memberAssociationUserRoles[0]
                              ? user.memberAssociationUserRoles[0].role.id
                              : 0,
                          )}
                          options={loadRoles(
                            roles,
                            user.memberAssociationUserRoles,
                            user.memberAssociationUserRoles[0]
                              ? user.memberAssociationUserRoles[0].role.id
                              : 0,
                          )}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <Select
                          small
                          value={
                            user.memberAssociationUserRoles[1]
                              ? user.memberAssociationUserRoles[1].role
                              : null
                          }
                          onChange={handleChangeRole(
                            user.id,
                            user.memberAssociationUserRoles[1]
                              ? user.memberAssociationUserRoles[1].role.id
                              : 0,
                          )}
                          options={loadRoles(
                            roles,
                            user.memberAssociationUserRoles,
                            user.memberAssociationUserRoles[1]
                              ? user.memberAssociationUserRoles[1].role.id
                              : 0,
                          )}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <Select
                          small
                          value={
                            user.memberAssociationUserRoles[2]
                              ? user.memberAssociationUserRoles[2].role
                              : null
                          }
                          onChange={handleChangeRole(
                            user.id,
                            user.memberAssociationUserRoles[2]
                              ? user.memberAssociationUserRoles[2].role.id
                              : 0,
                          )}
                          options={loadRoles(
                            roles,
                            user.memberAssociationUserRoles,
                            user.memberAssociationUserRoles[2]
                              ? user.memberAssociationUserRoles[2].role.id
                              : 0,
                          )}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <Table.Label>{t('page.ma.peopleUsers.maFrom')}:</Table.Label>
                        {user.memberAssociationUserRoles[0].dateFrom &&
                          formatDate(user.memberAssociationUserRoles[0].dateFrom)}
                      </Table.Cell>
                      <Table.Cell>
                        <ButtonAction
                          onClick={() => setRemoveConfirmModal(user.id)}
                          title={t('global.removeButton')}
                          icon="closeCircle"
                        />
                      </Table.Cell>
                    </Table.Row>
                  ))
                : null}
            </Table.Body>
          </Table>
          <Pagination styled currentPage={page} totalPages={pages} onChange={setPage} />
        </PageContent>
      </WideContainer>
      <Modal
        heading={t('page.ma.peopleUsers.modalRemoveHeading')}
        isOpen={!!removeConfirmModal}
        confirm={() => handleDeleteUser(removeConfirmModal)() && setRemoveConfirmModal(null)}
        cancel={() => setRemoveConfirmModal(null)}
      />
    </>
  );
}

Users.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  page: PropTypes.number.isRequired,
  pages: PropTypes.number.isRequired,
  sort: PropTypes.string.isRequired,
  perPage: PropTypes.number.isRequired,
  loadUsers: PropTypes.func.isRequired,
  roles: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.number }))
    .isRequired,
  loadGlobalMaRoles: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  changeRole: PropTypes.func.isRequired,
  deleteUser: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  downloadReport: PropTypes.func.isRequired,
  filters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setFilters: PropTypes.func.isRequired,
};

Users.defaultProps = {};

const mapStateToProps = ({ ma, globals, loading }) => ({
  ...ma.people.users,
  roles: globals.ma.roles,
  loading: !!loading.filter((item) => item.type === 'MA').length,
});

const mapDispatchToProps = (dispatch) => ({
  loadUsers: () => dispatch(loadMaPeopleUsersCreator()),
  loadGlobalMaRoles: () => dispatch(loadGlobalMaRolesCreator()),
  setSort: (payload) => dispatch(setMaPeopleUsersSortCreator(payload)),
  changeRole: (payload) => dispatch(changeMaPeopleUsersRoleCreator(payload)),
  deleteUser: (payload) => dispatch(deleteMaPeopleUserCreator(payload)),
  setPage: (payload) => dispatch(setMaPeopleUsersPageCreator(payload)),
  setPerPage: (payload) => dispatch(setMaPeopleUsersPerPageCreator(payload)),
  downloadReport: () => dispatch(downloadMaPeopleReportCreator('users')),
  setFilters: (payload) => dispatch(setMaPeopleUsersFiltersCreator(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Users);
